// ConfirmContext: the in-app replacement for window.confirm, on the kit Dialog. An imperative hook
// returns a Promise<boolean>, so a call site changes almost mechanically:
//
//   if (!window.confirm('Delete this?')) return;
//   const confirm = useConfirm();
//   if (!(await confirm({ message: 'Delete this?' }))) return;
//
// One question at a time: asking a second resolves the first as false. Escape, the scrim and
// Cancel resolve false. Enter is not bound globally: the focused key answers it, and a danger
// question starts on Cancel, so a destructive action is never one stray Enter away. The dialog
// closes on the answer; any work after it, and its busy state, are the caller's.
// docs/operations/design-standard.md, "Controls".

import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Button } from '../ui/Button';
import Dialog from '../ui/Dialog';
import TypeToConfirm from '../ui/TypeToConfirm';

export interface ConfirmOptions {
  title?: string;
  /** A string keeps its line breaks; anything else renders as given. */
  message?: React.ReactNode;
  /** Default "Confirm". */
  confirmLabel?: string;
  /** Default "Cancel". */
  cancelLabel?: string;
  /** `danger` sets the title and the key in the error tone and starts focus on Cancel. */
  tone?: 'default' | 'danger';
  /**
   * For what cannot be undone: the words to type back before the key arms (the record's name,
   * serial or SKU). The key becomes the one solid red key.
   */
  phrase?: string;
  /** What the phrase is, for the prompt: "the vendor's name". */
  phraseNoun?: string;
}

type ConfirmFn = (options?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Returns an async confirm(options) => Promise<boolean>. Must be used within a ConfirmProvider. */
export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
};

interface Pending extends ConfirmOptions {
  resolve: (value: boolean) => void;
  /** One per question: a second question replaces the first dialog rather than reusing it. */
  key: number;
}

let asked = 0;

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<Pending | null>(null);
  // The last question stays drawn while its dialog fades out.
  const [shownQuestion, setShownQuestion] = useState<Pending | null>(null);
  const confirmKey = useRef<HTMLButtonElement>(null);
  const cancelKey = useRef<HTMLButtonElement>(null);

  const confirm = useCallback<ConfirmFn>(
    (options = {}) =>
      new Promise<boolean>((resolve) => {
        const next: Pending = { ...options, resolve, key: ++asked };
        setPending((prev) => {
          if (prev) prev.resolve(false); // never leave a stacked promise hanging
          return next;
        });
        setShownQuestion(next);
      }),
    [],
  );

  const answer = useCallback((result: boolean) => {
    setPending((prev) => {
      if (prev) prev.resolve(result);
      return null;
    });
  }, []);

  const q = pending || shownQuestion;
  const danger = q?.tone === 'danger';
  const message = q?.message;
  const body = typeof message === 'string'
    ? <p className={`m-0 whitespace-pre-wrap text-sm leading-5 ${q?.title ? 'text-on-surface-variant' : 'text-on-surface'}`}>{message}</p>
    : message;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {q && q.phrase ? (
        <TypeToConfirm
          key={q.key}
          open={!!pending}
          title={q.title || 'Confirm'}
          phrase={q.phrase}
          phraseNoun={q.phraseNoun}
          confirmLabel={q.confirmLabel || 'Confirm'}
          cancelLabel={q.cancelLabel}
          onCancel={() => answer(false)}
          onConfirm={() => answer(true)}
        >
          {body}
        </TypeToConfirm>
      ) : q ? (
        <Dialog
          key={q.key}
          open={!!pending}
          onClose={() => answer(false)}
          title={q.title}
          label={q.title ? undefined : q.confirmLabel || 'Confirm'}
          tone={danger ? 'danger' : 'default'}
          alert
          size="md"
          hideClose
          initialFocus={danger ? cancelKey : confirmKey}
          onExited={() => setShownQuestion((s) => (s === q ? null : s))}
          footer={(
            <>
              <Button ref={cancelKey} variant="tonal" onClick={() => answer(false)}>{q.cancelLabel || 'Cancel'}</Button>
              <Button ref={confirmKey} variant={danger ? 'danger' : 'filled'} onClick={() => answer(true)}>
                {q.confirmLabel || 'Confirm'}
              </Button>
            </>
          )}
        >
          {body}
        </Dialog>
      ) : null}
    </ConfirmContext.Provider>
  );
};
